import React, { useState, useEffect } from 'react';
import { 
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  Paper, CircularProgress, Snackbar, Alert, TextField,
  IconButton, FormControl, InputLabel, Select, MenuItem, Tab, Tabs
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import FilterListIcon from '@mui/icons-material/FilterList';
import PageHeader from '../components/PageHeader';
import { 
  getTeacherSubjects, createTeacherSubject, updateTeacherSubject, deleteTeacherSubject,
  getTeachers, getSubjects, getDepartments
} from '../services/api';
import Autocomplete from '@mui/material/Autocomplete';

const TeacherSubjects = () => {
  const [teacherSubjects, setTeacherSubjects] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [filteredSubjects, setFilteredSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [currentTeacherSubject, setCurrentTeacherSubject] = useState({
    teacher_id: '',
    subject_id: '',
    department_id: '',
    section_preferences: ''
  });
  const [selectedId, setSelectedId] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [subjectFilter, setSubjectFilter] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [activeTab, setActiveTab] = useState(0);

  const columns = [
    { field: 'teacher_subject_id', headerName: 'ID', width: 70 },
    { field: 'teacher_name', headerName: 'Teacher', flex: 1 },
    { field: 'subject_name', headerName: 'Subject', width: 200 },
    { field: 'subject_code', headerName: 'Subject Code', width: 150 },
    { field: 'department_name', headerName: 'Department', width: 180 },
    { 
      field: 'is_lab', 
      headerName: 'Lab', 
      width: 80, 
      renderCell: (params) => (
        params.row.is_lab ? 'Yes' : 'No'
      )
    },
    {
      field: 'section_preferences',
      headerName: 'Section Preferences',
      width: 180,
      renderCell: (params) => (
        params.row.section_preferences || 'All Sections'
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      sortable: false,
      width: 120,
      renderCell: (params) => (
        <Box>
          <IconButton 
            color="primary" 
            size="small"
            onClick={() => handleEdit(params.row)}
          >
            <EditIcon />
          </IconButton>
          <IconButton 
            color="error" 
            size="small"
            onClick={() => handleDeleteClick(params.row.teacher_subject_id)}
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      )
    }
  ];

  // Use useEffect to properly handle state changes and data fetching
  useEffect(() => {
    fetchInitialData();
  }, []); // Only run once on component mount

  // Add separate effect for tab changes
  useEffect(() => {
    if (loading) return; // Skip during initial loading
    fetchFilteredData();
  }, [activeTab]); // Re-run when tab changes

  // Add separate effect for filter changes
  useEffect(() => {
    if (loading) return; // Skip during initial loading
    // Only fetch if filters have actually been applied
    if (departmentFilter || subjectFilter) {
      fetchFilteredData();
    }
  }, [departmentFilter, subjectFilter]); // Re-run when filters change

  // Initial data fetch
  const fetchInitialData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        fetchTeachers(),
        fetchSubjects(),
        fetchDepartments()
      ]);
      // Fetch teacher-subjects last, after other data is loaded
      await fetchFilteredData();
    } finally {
      setLoading(false);
    }
  };

  // Core function to fetch data with current filters
  const fetchFilteredData = async () => {
    try {
      // Build query parameters for API call
      const params = {};
      
      // 1. Apply department filter
      if (departmentFilter) {
        params.department_id = departmentFilter;
      }
      
      // 2. Apply tab-specific filters
      if (activeTab === 1) {
        // Theory tab (is_lab = 0)
        params.is_lab = 0;
        console.log('Tab 1: Fetching THEORY subjects (is_lab=0)');
      } else if (activeTab === 2) {
        // Lab tab (is_lab = 1)
        params.is_lab = 1;
        console.log('Tab 2: Fetching LAB subjects (is_lab=1)');
      } else {
        // All tab (no is_lab filter)
        console.log('Tab 0: Fetching ALL subjects');
      }
      
      // Log what we're about to fetch
      console.log('API request params:', params);
      
      // Execute the API call
      const response = await getTeacherSubjects(params);
      let mappings = response.data;
      console.log(`Received ${mappings.length} mappings from API`);

      // Apply client-side filtering for subject search
      if (subjectFilter && subjectFilter.trim() !== '') {
        const searchTerm = subjectFilter.toLowerCase().trim();
        const filteredMappings = mappings.filter(item => {
          const subjectName = item.subject_name?.toLowerCase() || '';
          const subjectCode = item.subject_code?.toLowerCase() || '';
          return subjectName.includes(searchTerm) || subjectCode.includes(searchTerm);
        });
        
        console.log(`Filtered from ${mappings.length} to ${filteredMappings.length} by search term '${searchTerm}'`);
        mappings = filteredMappings;
      }

      // Update state with final filtered results
      setTeacherSubjects(mappings);
    } catch (error) {
      console.error('Error fetching data:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load teacher-subject mappings',
        severity: 'error'
      });
    }
  };

  const fetchTeachers = async () => {
    try {
      const response = await getTeachers();
      setTeachers(response.data);
    } catch (error) {
      console.error('Error fetching teachers:', error);
    }
  };

  const fetchSubjects = async () => {
    try {
      const response = await getSubjects();
      setSubjects(response.data);
      setFilteredSubjects(response.data);
    } catch (error) {
      console.error('Error fetching subjects:', error);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await getDepartments();
      setDepartments(response.data);
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  const filterSubjectsByDepartment = (departmentId) => {
    if (!departmentId) {
      setFilteredSubjects(subjects);
    } else {
      setFilteredSubjects(subjects.filter(subject => 
        subject.department_id === parseInt(departmentId) || !subject.department_id
      ));
    }
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
    setCurrentTeacherSubject({
      teacher_id: '',
      subject_id: '',
      department_id: '',
      section_preferences: ''
    });
    setSelectedId(null);
    setFormErrors({});
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleEdit = (teacherSubject) => {
    // Create a clean edit state with the correct values
    const editData = {
      teacher_id: teacherSubject.teacher_id,
      subject_id: teacherSubject.subject_id,
      department_id: teacherSubject.subject_dept_id || '',
      section_preferences: teacherSubject.section_preferences || ''
    };
    
    // Update state
    setCurrentTeacherSubject(editData);
    setSelectedId(teacherSubject.teacher_subject_id);
    setOpenDialog(true);
    setFormErrors({});
    
    // Filter subjects based on selected department
    if (editData.department_id) {
      filterSubjectsByDepartment(editData.department_id);
    } else {
      setFilteredSubjects(subjects);
    }
    
    console.log('Editing mapping:', teacherSubject);
  };

  const handleDeleteClick = (id) => {
    setSelectedId(id);
    setConfirmDelete(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteTeacherSubject(selectedId);
      setTeacherSubjects(teacherSubjects.filter(ts => ts.teacher_subject_id !== selectedId));
      setSnackbar({
        open: true,
        message: 'Teacher-Subject mapping deleted successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error deleting teacher-subject mapping:', error);
      setSnackbar({
        open: true,
        message: 'Failed to delete teacher-subject mapping',
        severity: 'error'
      });
    } finally {
      setConfirmDelete(false);
      setSelectedId(null);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentTeacherSubject({ ...currentTeacherSubject, [name]: value });
    
    // Clear the error for this field
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: ''
      });
    }

    // If department changes, filter subjects accordingly
    if (name === 'department_id') {
      filterSubjectsByDepartment(value);
    }
  };
  
  // Filter dialog management
  const handleOpenFilterDialog = () => {
    // Open with current filter values
    setFilterDialogOpen(true);
  };

  const handleCloseFilterDialog = () => {
    setFilterDialogOpen(false);
  };

  // Apply the filters from dialog
  const applyFilter = () => {
    // Filters will be applied through the useEffect hook
    handleCloseFilterDialog();
  };

  // Reset filters to default
  const resetFilter = () => {
    // Clear filter values
    setSubjectFilter('');
    setDepartmentFilter('');
    
    // Close dialog - the useEffect hook will handle data fetch
    handleCloseFilterDialog();
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    // Just update the tab - the useEffect hook will handle data fetch
    setActiveTab(newValue);
  };

  const validateForm = () => {
    const errors = {};
    if (!currentTeacherSubject.teacher_id) {
      errors.teacher_id = 'Teacher is required';
    }
    if (!currentTeacherSubject.subject_id) {
      errors.subject_id = 'Subject is required';
    }
    return errors;
  };

  const handleSubmit = async () => {
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    try {
      // Find teacher and subject details for display
      const teacher = teachers.find(t => t.teacher_id === parseInt(currentTeacherSubject.teacher_id));
      const subject = subjects.find(s => s.subject_id === parseInt(currentTeacherSubject.subject_id));
      const department = departments.find(d => d.department_id === parseInt(currentTeacherSubject.department_id));
      
      if (selectedId) {
        // Update existing teacher-subject mapping
        await updateTeacherSubject(selectedId, currentTeacherSubject);
        
        setTeacherSubjects(teacherSubjects.map(ts => 
          ts.teacher_subject_id === selectedId 
            ? { 
                ...ts, 
                ...currentTeacherSubject, 
                teacher_subject_id: selectedId,
                teacher_name: teacher?.name,
                subject_name: subject?.subject_name,
                subject_code: subject?.subject_code,
                department_name: department?.department_name,
                is_lab: subject?.is_lab,
                subject_dept_id: subject?.department_id
              } 
            : ts
        ));
        
        setSnackbar({
          open: true,
          message: 'Teacher-Subject mapping updated successfully',
          severity: 'success'
        });
      } else {
        // Create new teacher-subject mapping
        const response = await createTeacherSubject(currentTeacherSubject);
        
        const newTeacherSubject = {
          ...currentTeacherSubject,
          teacher_subject_id: response.data.teacher_subject_id,
          teacher_name: teacher?.name,
          subject_name: subject?.subject_name,
          subject_code: subject?.subject_code,
          department_name: department?.department_name,
          is_lab: subject?.is_lab,
          subject_dept_id: subject?.department_id
        };
        
        setTeacherSubjects([...teacherSubjects, newTeacherSubject]);
        setSnackbar({
          open: true,
          message: 'Teacher-Subject mapping created successfully',
          severity: 'success'
        });
      }
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving teacher-subject mapping:', error);
      setSnackbar({
        open: true,
        message: 'Failed to save teacher-subject mapping',
        severity: 'error'
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box>
      <PageHeader 
        title="Teacher-Subject Mappings" 
        subtitle="Assign teachers to subjects" 
        action={
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button 
              variant="outlined" 
              startIcon={<FilterListIcon />}
              onClick={handleOpenFilterDialog}
            >
              Filter
            </Button>
            <Button 
              variant="contained" 
              startIcon={<AddIcon />}
              onClick={handleOpenDialog}
            >
              Add Mapping
            </Button>
          </Box>
        }
      />

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab label="All Mappings" />
          <Tab label="Theory Mappings" />
          <Tab label="Lab Mappings" />
        </Tabs>
      </Box>

      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (
          <DataGrid
            rows={teacherSubjects}
            columns={columns}
            getRowId={(row) => row.teacher_subject_id}
            initialState={{
              pagination: {
                paginationModel: { page: 0, pageSize: 10 },
              },
            }}
            pageSizeOptions={[5, 10, 25]}
            checkboxSelection={false}
            disableSelectionOnClick
            autoHeight
          />
        )}
      </Paper>

      {/* Filter Dialog */}
      <Dialog open={filterDialogOpen} onClose={handleCloseFilterDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Filter Teacher-Subject Mappings</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              margin="normal"
              label="Subject Search"
              value={subjectFilter}
              onChange={(e) => setSubjectFilter(e.target.value)}
              placeholder="Search by subject name or code"
            />
            <FormControl fullWidth margin="normal">
              <InputLabel>Department</InputLabel>
              <Select
                value={departmentFilter}
                label="Department"
                onChange={(e) => setDepartmentFilter(e.target.value)}
              >
                <MenuItem value="">All Departments</MenuItem>
                {departments.map((dept) => (
                  <MenuItem key={dept.department_id} value={dept.department_id}>
                    {dept.department_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={resetFilter}>Reset</Button>
          <Button onClick={applyFilter} variant="contained" color="primary">
            Apply Filters
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add/Edit Teacher-Subject Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{selectedId ? 'Edit Teacher-Subject Mapping' : 'Add Teacher-Subject Mapping'}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <FormControl fullWidth margin="normal">
              <InputLabel>Department</InputLabel>
              <Select
                name="department_id"
                value={currentTeacherSubject.department_id}
                label="Department"
                onChange={handleInputChange}
              >
                <MenuItem value="">Select Department</MenuItem>
                {departments.map((dept) => (
                  <MenuItem key={dept.department_id} value={dept.department_id}>
                    {dept.department_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            {/* Teacher Autocomplete with search */}
            <Autocomplete
              fullWidth
              options={teachers}
              getOptionLabel={(option) => option?.name || ''}
              value={teachers.find(t => t.teacher_id === parseInt(currentTeacherSubject.teacher_id)) || null}
              onChange={(event, newValue) => {
                handleInputChange({
                  target: {
                    name: 'teacher_id',
                    value: newValue ? newValue.teacher_id : ''
                  }
                });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Teacher"
                  margin="normal"
                  error={!!formErrors.teacher_id}
                  helperText={formErrors.teacher_id}
                />
              )}
            />

            {/* Subject Autocomplete with search */}
            <Autocomplete
              fullWidth
              options={filteredSubjects}
              getOptionLabel={(option) => option ? `${option.subject_name} (${option.subject_code})${option.is_lab ? ' - Lab' : ''}` : ''}
              value={filteredSubjects.find(s => s.subject_id === parseInt(currentTeacherSubject.subject_id)) || null}
              onChange={(event, newValue) => {
                handleInputChange({
                  target: {
                    name: 'subject_id',
                    value: newValue ? newValue.subject_id : ''
                  }
                });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Subject"
                  margin="normal"
                  error={!!formErrors.subject_id}
                  helperText={formErrors.subject_id}
                />
              )}
            />
            
            <TextField
              fullWidth
              margin="normal"
              name="section_preferences"
              label="Section Preferences (optional)"
              value={currentTeacherSubject.section_preferences}
              onChange={handleInputChange}
              placeholder="Example: A,B (leave empty for all sections)"
              helperText="Enter comma-separated section names that this teacher prefers to teach for this subject"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Delete Dialog */}
      <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this teacher-subject mapping? This action cannot be undone.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(false)}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TeacherSubjects;
